import type { ReactNode } from 'react';
import { isBottomNavActive } from '../lib/workspaceBehavior';

export type AppView = 'home' | 'setlists' | 'setlist' | 'library' | 'inbox' | 'map';

export function BrandMark() {
  return (
    <span className="brand-mark" aria-hidden="true">
      <img src="/icons/icon-192.png?v=2" alt="" />
    </span>
  );
}

export function ScreenHeader({
  eyebrow,
  title,
  home,
  onHome,
  onMenu,
}: {
  eyebrow: string;
  title: string;
  home?: boolean;
  onHome: () => void;
  onMenu: () => void;
}) {
  return (
    <header className="screen-header">
      {home ? (
        <BrandMark />
      ) : (
        <button
          type="button"
          className="icon-button"
          onClick={onHome}
          aria-label="Torna alla Home"
        >
          <svg className="home-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 10.8 12 3l9 7.8v9.7a.5.5 0 0 1-.5.5H15v-6H9v6H3.5a.5.5 0 0 1-.5-.5v-9.7Z" />
          </svg>
        </button>
      )}
      <div>
        <p>{eyebrow}</p>
        <h1>{title}</h1>
      </div>
      <button
        type="button"
        className="menu-button"
        onClick={onMenu}
        aria-label="Apri menu"
      >
        <i />
        <i />
        <i />
      </button>
    </header>
  );
}

export function BottomNav({
  current,
  onChange,
  canOpenMap = false,
}: {
  current: AppView;
  onChange: (view: AppView) => void;
  canOpenMap?: boolean;
}) {
  return (
    <nav className="bottom-nav" aria-label="Navigazione principale">
      <button
        type="button"
        className={isBottomNavActive(current, 'setlists') ? 'active' : ''}
        onClick={() => onChange('setlists')}
      >
        <span aria-hidden="true">≡</span>Scalette
      </button>
      <button
        type="button"
        className={isBottomNavActive(current, 'library') ? 'active' : ''}
        onClick={() => onChange('library')}
      >
        <span aria-hidden="true">♫</span>Brani
      </button>
      <button
        type="button"
        className={isBottomNavActive(current, 'map') ? 'active' : ''}
        onClick={() => onChange('map')}
        disabled={!canOpenMap && current !== 'map'}
        aria-label={current === 'map' ? 'Torna alla vista scaletta' : 'Apri la mappa'}
      >
        <span aria-hidden="true">⌖</span>Mappa
      </button>
    </nav>
  );
}

export function Sheet({
  title,
  eyebrow,
  onClose,
  children,
  centered = false,
  className = '',
}: {
  title: string;
  eyebrow?: string;
  onClose: () => void;
  children: ReactNode;
  centered?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`overlay ${centered ? 'centered' : ''} ${className}`.trim()}
      onPointerDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section className="sheet" role="dialog" aria-modal="true" aria-label={title}>
        <span className="sheet-handle" />
        <header>
          <div>
            {eyebrow && <p>{eyebrow}</p>}
            <h2>{title}</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Chiudi"
          >
            ×
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

export function EmptyState({
  icon = '♪',
  title,
  children,
  action,
}: {
  icon?: string;
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span aria-hidden="true">{icon}</span>
      <h2>{title}</h2>
      <p>{children}</p>
      {action}
    </div>
  );
}

export function Toast({
  children,
  error = false,
}: {
  children: ReactNode;
  error?: boolean;
}) {
  return (
    <div className={`toast ${error ? 'error' : ''}`} role="status">
      {error ? '!' : '✓'} {children}
    </div>
  );
}
